import * as React from 'react';
import styled, { css } from 'styled-components';

import Typography from '@components/basics/typography';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';

export interface DropdownHeaderProps {
  className?: string;
  title?: React.ReactNode;
  label?: string;
  renderImage?: any;
  listOpen?: boolean;
  hasSelected?: boolean;
  error?: string;
  avatar?: string;
  disabled?: boolean;
  noShadow?: boolean;
  customBorderRadius?: string;
  country?: string;
  isGrey?: boolean;
  isDark?: boolean;
  isDarker?: boolean;
  decoration?: React.ReactElement;
  smallDecoration?: boolean;
  redDot?: boolean;
  customIcon?: React.ReactNode;
  hasPillShape?: boolean;
  hasChanges?: boolean;
  capitaliseChildren?: boolean;
  toggleOpen: () => void;
  forwardedRef?: React.ForwardedRef<HTMLDivElement>;

  showSelectableOption?: boolean;
}

export const DropdownHeader: React.FC<DropdownHeaderProps> = (props) => {
  return (
    <div
      ref={props.forwardedRef}
      onClick={props.toggleOpen}
      className={props.className}
    >
      {props.decoration ? (
        <div className="decoration">{props.decoration}</div>
      ) : null}
      <div className="dropdown-text-wrapper">
        <Row>
          {props.renderImage() ? props.renderImage() : null}
          <Typography
            className={`dropdown-text ${
              props.isDark || (props.hasPillShape && props.hasChanges)
                ? 'dark'
                : props.isDarker
                ? 'darker'
                : ''
            } ${
              props.showSelectableOption && !props.hasSelected
                ? 'not-selected-option'
                : ''
            }`}
            weight="700"
          >
            {props.label || props.title}
          </Typography>
        </Row>
        <SelectedItem
          className={`label ${
            props.isDark || (props.hasPillShape && props.hasChanges)
              ? 'dark'
              : props.isDarker
              ? 'darker'
              : ''
          }`}
        >
          {props.title}
        </SelectedItem>
      </div>
      {props.error ? <StyledError>{props.error}</StyledError> : null}
      {props.disabled ? null : (
        <IconWrapper>
          {props.redDot ? <div className="red-dot"></div> : null}
          {props.customIcon ? (
            props.customIcon
          ) : (
            <CircleArrowIcon
              isDark={props.isDark || (props.hasPillShape && props.hasChanges)}
              darkCustomBgColor={props.hasPillShape || props.isGrey}
              size="24px"
              arrowUp={props.listOpen}
            />
          )}
        </IconWrapper>
      )}
    </div>
  );
};

export default styled(DropdownHeader)`
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  min-height: 48px;
  padding: ${(props) =>
    props.hasSelected ? '24px 40px 9px 9px' : '17px 40px 16px 9px'};
  border-radius: ${(props) =>
    props.customBorderRadius ? props.customBorderRadius : '4px'};
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-left: 3px solid transparent;
  transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    padding
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  position: relative;

  @media ${({ theme }) => theme.device.tablet} {
    height: 60px;
    padding: ${(props) =>
      props.hasSelected ? '30px 50px 14px 15px' : '22px 50px 22px 15px'};
  }

  ${(props) =>
    props.isGrey
      ? css`
          background-color: ${({ theme }) =>
            theme.colors.inputBackgroundAlternate};
        `
      : null}

  ${(props) =>
    props.isDark
      ? css`
          background-color: ${({ theme }) => theme.colors.primary};
        `
      : null}
  ${(props) =>
    props.isDarker
      ? css`
          background-color: ${({ theme }) => theme.colors.darkDropdown};
        `
      : null}

  .dropdown-text-wrapper {
    width: 100%;
    overflow: hidden;

    .dropdown-text {
      color: ${({ theme }) => theme.colors.primary};
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-size: ${({ theme }) => theme.typography.h4};
      line-height: 16px;
      height: 18px;
      max-width: 220px;
      ${(props) =>
        props.disabled
          ? css`
              opacity: 0.6;
            `
          : null}

      &.dark {
        color: ${({ theme }) => theme.colors.background};
      }

      &.not-selected-option {
        color: ${({ theme }) => theme.colors.secondaryDark};
      }

      ${(props) =>
        props.capitaliseChildren
          ? css`
              text-transform: capitalize;
            `
          : null}

      @media ${({ theme }) => theme.device.mobileM} {
        max-width: none;
      }

      ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
        font-size: ${({ theme }) => theme.typography.h4};
        line-height: 16px;
        height: auto;
      }
    }
  }

  ${(props) =>
    props.decoration
      ? css`
          .decoration {
            position: absolute;
          }
        `
      : null}

  ${(props) =>
    props.decoration && !props.smallDecoration
      ? css`
          .decoration {
            top: 50%;
            left: 18px;
            transform: translate(0, -50%);
            max-height: 32px;
          }
        `
      : null}

  ${(props) =>
    props.decoration && props.smallDecoration
      ? css`
          .decoration {
            bottom: 11px;
            left: 9px;

            @media ${({ theme }) => theme.device.tablet} {
              bottom: 14px;
              left: 18px;
            }
          }
        `
      : null}

  .label {
    opacity: 0;
  }

  ${(props) =>
    props.error
      ? css`
          border-color: ${({ theme }) => theme.colors.error};
        `
      : null}

  ${(props) =>
    props.listOpen
      ? props.isDark
        ? css`
            background-color: ${({ theme }) => theme.colors.primary};
          `
        : css`
            border-radius: 2px 2px 0 0;
            /* background-color: ${({ theme }) =>
              theme.colors.iconBackground}; */
          `
      : null}

  ${(props) =>
    props.listOpen && props.customBorderRadius
      ? css`
          border-radius: 0 2px 2px 0;
        `
      : null}

  .label {
    font-size: ${(props) =>
      props.hasSelected
        ? css`
            ${({ theme }) => theme.typography.span}
          `
        : css`
            ${({ theme }) => theme.typography.h3}
          `};
    top: ${(props) => (props.hasSelected ? '7px' : '14px')};
    left: 9px;
    opacity: ${(props) => (props.hasSelected ? '.6' : '0')};

    @media ${({ theme }) => theme.device.tablet} {
      font-size: ${(props) =>
        props.hasSelected
          ? css`
              ${({ theme }) => theme.typography.span}
            `
          : css`
              ${({ theme }) => theme.typography.h4}
            `};
      top: ${(props) => (props.hasSelected ? '12px' : '19px')};
      left: 15px;
    }
  }

  ${(props) =>
    props.decoration && !props.smallDecoration
      ? css`
          padding-left: 56px !important;
          background-color: ${({ theme }) => theme.colors.inputBackground};

          .label {
            left: 56px;
          }
        `
      : null}

  ${(props) =>
    props.decoration && props.smallDecoration
      ? css`
          padding-left: 35px !important;
        `
      : null}

  ${(props) =>
    props.disabled
      ? css`
          user-select: none;
        `
      : null}

${(props) =>
    !props.title
      ? css`
          padding-top: 16px;
          padding-bottom: 16px;
          @media ${({ theme }) => theme.device.tablet} {
            padding-top: 16px;
            padding-bottom: 16px;
          }
        `
      : null}

  ${(props) =>
    props.hasPillShape
      ? css`
          border-radius: 20px 20px 20px 20px;
          background-color: ${({ theme }) =>
            theme.colors.inputBackgroundAlternate};
          color: ${({ theme }) => theme.colors.primary};
          transition: border-radius
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};
          @media ${({ theme }) => theme.device.tablet} {
            border-radius: 32px 32px 32px 32px;
            padding-top: 16px;
            padding-bottom: 16px;
          }

          .decoration {
            max-height: 18px;
          }
        `
      : null}

  ${(props) =>
    props.hasPillShape && props.listOpen
      ? css`
          border-radius: 20px 20px 0 0;
          @media ${({ theme }) => theme.device.tablet} {
            border-radius: 32px 32px 0 0;
          }
        `
      : null}

  ${(props) =>
    props.hasPillShape && props.hasChanges
      ? css`
          background-color: ${({ theme }) => theme.colors.primary};
          .dropdown-text-wrapper {
            .dropdown-text {
              color: ${({ theme }) => theme.colors.differentBackground};
            }
          }
        `
      : null}
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translate(0, -50%);

  @media ${({ theme }) => theme.device.tablet} {
    right: 18px;
    max-height: 24px;
  }

  .red-dot {
    height: 6px;
    width: 6px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.accent};
    position: absolute;
    right: 0px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const SelectedItem = styled(Typography)`
  color: ${({ theme }) => theme.colors.primary};

  &.dark {
    color: ${({ theme }) => theme.colors.differentBackground};
  }

  position: absolute;
  pointer-events: none;
  transition: top
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    left
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
`;

const StyledError = styled(Typography).attrs(() => ({
  type: 'small',
  weight: '700',
}))`
  position: absolute;
  top: 50%;
  right: 58px;
  text-align: right;
  transform: translate(0, -50%);
  color: ${({ theme }) => theme.colors.error};
`;
